import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Home, MessageSquare, CreditCard, User, LogOut,
  Plus, CheckCircle, Clock, AlertCircle, Menu, Star, Reply, Trash2, Edit3, Calendar, IndianRupee
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { partnerAPI, reviewsAPI, bookingsAPI } from '../../lib/api';
import ImageUpload from '../../components/ImageUpload';
import PaymentModal from '../../components/PaymentModal';
import StarRating from '../../components/StarRating';

// ---- Inline Reply Component ----
const ReviewReplyRow = ({ review, onUpdate }) => {
  const [text, setText] = React.useState(review.reply || '');
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showForm, setShowForm] = React.useState(false);

  const handleSave = async () => {
    if (!text.trim()) { setError('Reply cannot be empty'); return; }
    setSaving(true); setError('');
    try {
      await reviewsAPI.addReply(review.id, text.trim());
      onUpdate(review.id, { reply: text.trim(), replied_at: new Date().toISOString() });
      setShowForm(false);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await reviewsAPI.deleteReply(review.id);
      onUpdate(review.id, { reply: null, replied_at: null });
      setText('');
    } catch (e) { setError(e.message); }
    finally { setDeleting(false); }
  };

  if (review.status !== 'approved') return null;

  return (
    <div className="mt-3 border-t border-gray-50 pt-3">
      {review.reply && !showForm ? (
        <div>
          <div className="bg-[#FAFAF9] rounded-xl p-3 border border-gray-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-navy">Your reply</span>
              <div className="flex items-center gap-2">
                <button onClick={() => { setText(review.reply); setShowForm(true); }}
                  className="text-xs text-gray-400 hover:text-navy flex items-center gap-1 transition-colors">
                  <Edit3 size={11} /> Edit
                </button>
                <button onClick={handleDelete} disabled={deleting}
                  className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors">
                  <Trash2 size={11} /> {deleting ? '...' : 'Delete'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600">{review.reply}</p>
          </div>
        </div>
      ) : !showForm ? (
        <button
          data-testid={`reply-btn-${review.id}`}
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs font-medium text-navy hover:text-gold transition-colors"
        >
          <Reply size={13} /> Reply to this review
        </button>
      ) : null}

      {showForm && (
        <div className="mt-2">
          <textarea
            data-testid={`reply-textarea-${review.id}`}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Write a professional, helpful reply to this guest review..."
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold resize-none transition-colors"
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          <div className="flex items-center gap-2 mt-2">
            <button
              data-testid={`submit-reply-${review.id}`}
              onClick={handleSave}
              disabled={saving}
              className="bg-navy hover:bg-navy-light text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-60"
            >
              {saving ? <><div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> Posting...</> : <><Reply size={12} /> Post Reply</>}
            </button>
            <button onClick={() => { setShowForm(false); setText(review.reply || ''); setError(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 px-3 py-2 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const LOGO = "https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg";

const TYPE_LABELS = {
  homestay: 'Homestay', hotel: 'Hotel', pg: 'PG', hostel: 'Hostel',
  farmhouse: 'Farmhouse', penthouse: 'Penthouse',
  function_hall: 'Function Hall', banquet_hall: 'Banquet Hall'
};
const PROPERTY_TYPES = Object.keys(TYPE_LABELS);
const AMENITIES_LIST = ['pool', 'wifi', 'parking', 'bbq', 'ac', 'music', 'kitchen', 'gym', 'garden', 'catering', 'overnight'];

const ONBOARDING_FEES = {
  homestay: 799, hotel: 1499, pg: 999, hostel: 999,
  farmhouse: 1499, penthouse: 1499, function_hall: 1999, banquet_hall: 1999
};

// ---- Sidebar ----
const Sidebar = ({ active, setActive, sidebarOpen, setSidebarOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = [
    { key: 'overview', icon: <LayoutDashboard size={18} />, label: 'Overview' },
    { key: 'properties', icon: <Home size={18} />, label: 'Properties' },
    { key: 'add', icon: <Plus size={18} />, label: 'Add Property' },
    { key: 'enquiries', icon: <MessageSquare size={18} />, label: 'Enquiries' },
    { key: 'bookings', icon: <Calendar size={18} />, label: 'Bookings' },
    { key: 'reviews', icon: <Star size={18} />, label: 'Reviews' },
    { key: 'onboarding', icon: <CreditCard size={18} />, label: 'Onboarding Fee' },
    { key: 'profile', icon: <User size={18} />, label: 'Profile' },
  ];
  return (
    <>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-navy z-40 transition-transform duration-300 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b border-white/10">
          <img src={LOGO} alt="HYGAD" className="h-9 mb-1" />
          <p className="text-white/40 text-xs">Partner Dashboard</p>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(({ key, icon, label }) => (
            <button key={key} onClick={() => { setActive(key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 ${
                active === key ? 'bg-gold/20 text-gold' : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}>
              {icon} {label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="px-4 py-3 mb-2">
            <div className="text-white/80 text-sm font-medium truncate">{user?.name}</div>
            <div className="text-white/40 text-xs truncate">{user?.email}</div>
          </div>
          <button onClick={() => { logout(); navigate('/'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

const StatCard = ({ icon, label, value, color = 'gold' }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(10,22,40,0.06)' }}>
    <div className={`w-10 h-10 rounded-xl mb-4 flex items-center justify-center ${
      color === 'gold' ? 'bg-gold/10 text-gold' : color === 'green' ? 'bg-green-100 text-green-600' :
      color === 'amber' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-500'
    }`}>{icon}</div>
    <div className="text-2xl font-bold text-navy mb-0.5">{value ?? '—'}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

// ---- Add Property Form ----
const AddPropertyForm = ({ user, onSuccess }) => {
  const [form, setForm] = useState({
    name: '', description: '', type: 'homestay', experiences: ['stay'],
    location: { state: 'Telangana', district: '', city: '', area: '' },
    amenities: [], price_min: '', price_max: '', max_guests: '',
    images: [], rules: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showFeePaymentPage, setShowFeePaymentPage] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [submittedProperty, setSubmittedProperty] = useState(null);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const setLoc = (k) => (e) => setForm(p => ({ ...p, location: { ...p.location, [k]: e.target.value } }));
  const toggleExp = (exp) => setForm(p => ({
    ...p, experiences: p.experiences.includes(exp) ? p.experiences.filter(x => x !== exp) : [...p.experiences, exp]
  }));
  const toggleAmenity = (a) => setForm(p => ({
    ...p, amenities: p.amenities.includes(a) ? p.amenities.filter(x => x !== a) : [...p.amenities, a]
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const prop = await partnerAPI.createProperty({
        ...form,
        price_min: parseInt(form.price_min),
        price_max: parseInt(form.price_max),
        max_guests: parseInt(form.max_guests),
      });
      setSubmittedProperty(prop);
      setShowFeePaymentPage(true);
      setPaymentModalOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fee = ONBOARDING_FEES[form.type] || 999;

  if (showFeePaymentPage && submittedProperty) {
    return (
      <div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6 flex items-start gap-3">
          <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-green-800 text-sm">Property Submitted Successfully!</p>
            <p className="text-green-600 text-xs mt-0.5">
              "{submittedProperty.name}" has been submitted for review. Now pay the One-Time Partner Onboarding Fee to activate your listing.
            </p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-8 max-w-md" style={{ boxShadow: '0 1px 4px rgba(10,22,40,0.06)' }}>
          <p className="text-gold text-xs uppercase tracking-[0.2em] font-medium mb-2">One-Time Payment</p>
          <h3 className="font-serif text-2xl font-bold text-navy mb-1">Partner Onboarding Fee</h3>
          <p className="text-gray-500 text-sm mb-6">
            Pay the one-time verification and onboarding fee for your <strong>{TYPE_LABELS[submittedProperty.type]}</strong> listing.
          </p>
          <div className="flex items-baseline gap-2 mb-6">
            <span className="font-serif text-4xl font-black text-navy">₹{fee.toLocaleString('en-IN')}</span>
            <span className="text-gray-400 text-sm">one-time</span>
          </div>
          <div className="space-y-2 mb-6">
            {['Business Verification', 'Property Listing', 'Partner Dashboard', 'Booking Enquiries', 'Analytics & Reviews', 'Priority Support'].map(b => (
              <div key={b} className="flex items-center gap-2">
                <CheckCircle size={14} className="text-gold shrink-0" />
                <span className="text-sm text-gray-600">{b}</span>
              </div>
            ))}
          </div>
          <button
            data-testid="open-payment-modal"
            onClick={() => setPaymentModalOpen(true)}
            className="w-full bg-navy hover:bg-navy-light text-white py-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <CreditCard size={16} />
            Pay ₹{fee.toLocaleString('en-IN')} — Complete Onboarding
          </button>
          <button onClick={() => onSuccess()} className="w-full text-sm text-gray-400 hover:text-gray-600 mt-3 py-2">
            Pay later from Onboarding Fee tab
          </button>
        </div>
        <PaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          propertyType={submittedProperty.type}
          propertyId={submittedProperty.id}
          partnerName={user?.name}
          partnerEmail={user?.email}
          onSuccess={() => { setPaymentModalOpen(false); onSuccess(); }}
        />
      </div>
    );
  }

  return (
    <form data-testid="add-property-form" onSubmit={handleSubmit} className="space-y-8 max-w-2xl">
      {/* Basic Info */}
      <div>
        <h3 className="font-semibold text-navy text-lg mb-5">Basic Information</h3>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1.5">Property Name *</label>
            <input required type="text" value={form.name} onChange={set('name')} placeholder="e.g. Azure Farmhouse"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1.5">Description *</label>
            <textarea required value={form.description} onChange={set('description')} rows={3}
              placeholder="Describe your property..."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1.5">Property Type *</label>
              <select required value={form.type} onChange={set('type')}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold">
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1.5">Max Guests *</label>
              <input required type="number" value={form.max_guests} onChange={set('max_guests')} placeholder="20"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-2">Experiences *</label>
            <div className="flex gap-3">
              {['stay', 'celebrate'].map(exp => (
                <button key={exp} type="button" onClick={() => toggleExp(exp)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium capitalize border transition-colors ${
                    form.experiences.includes(exp) ? 'bg-navy text-white border-navy' : 'border-gray-200 text-gray-500 hover:border-gold'
                  }`}>{exp}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <h3 className="font-semibold text-navy text-lg mb-5">Location</h3>
        <div className="grid grid-cols-2 gap-4">
          {[{ k: 'state', l: 'State', p: 'Telangana' }, { k: 'district', l: 'District', p: 'Hyderabad' },
            { k: 'city', l: 'City *', p: 'Hyderabad', req: true }, { k: 'area', l: 'Area / Locality', p: 'Banjara Hills' }
          ].map(({ k, l, p, req }) => (
            <div key={k}>
              <label className="text-xs text-gray-500 font-medium block mb-1.5">{l}</label>
              <input type="text" value={form.location[k]} onChange={setLoc(k)} placeholder={p} required={req}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div>
        <h3 className="font-semibold text-navy text-lg mb-5">Pricing</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1.5">Minimum Price (₹) *</label>
            <input required type="number" value={form.price_min} onChange={set('price_min')} placeholder="5000"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1.5">Maximum Price (₹) *</label>
            <input required type="number" value={form.price_max} onChange={set('price_max')} placeholder="20000"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
          </div>
        </div>
      </div>

      {/* Amenities */}
      <div>
        <h3 className="font-semibold text-navy text-lg mb-5">Amenities</h3>
        <div className="flex flex-wrap gap-2">
          {AMENITIES_LIST.map(a => (
            <button key={a} type="button" onClick={() => toggleAmenity(a)}
              className={`px-3.5 py-2 rounded-lg text-sm capitalize border transition-colors ${
                form.amenities.includes(a) ? 'bg-navy text-white border-navy' : 'border-gray-200 text-gray-500 hover:border-gold'
              }`}>{a}</button>
          ))}
        </div>
      </div>

      {/* Photos */}
      <div>
        <h3 className="font-semibold text-navy text-lg mb-2">Property Photos</h3>
        <p className="text-xs text-gray-400 mb-4">Upload up to 5 photos. First photo will be the cover image.</p>
        <ImageUpload
          images={form.images}
          onChange={(urls) => setForm(p => ({ ...p, images: urls }))}
          maxImages={5}
        />
      </div>

      {/* Onboarding fee preview */}
      <div className="bg-gold/5 border border-gold/20 rounded-2xl p-5">
        <p className="text-xs text-gold uppercase tracking-[0.2em] font-medium mb-1">One-Time Partner Onboarding Fee</p>
        <p className="text-gray-500 text-xs mb-2">For {TYPE_LABELS[form.type]} property type</p>
        <p className="font-serif text-3xl font-black text-navy">₹{fee.toLocaleString('en-IN')}</p>
        <p className="text-gray-400 text-xs mt-1">Paid once after submitting. Includes business verification & listing.</p>
      </div>

      {error && <p className="text-red-500 text-sm bg-red-50 px-4 py-3 rounded-xl">{error}</p>}

      <button type="submit" disabled={submitting} data-testid="submit-property"
        className="bg-navy hover:bg-navy-light text-white px-8 py-4 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60 flex items-center gap-2">
        {submitting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
        {submitting ? 'Submitting...' : 'Submit Property & Pay Onboarding Fee'}
      </button>
    </form>
  );
};

// ---- Main Dashboard ----
const PartnerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [propSuccess, setPropSuccess] = useState(false);
  const [payModal, setPayModal] = useState({ open: false, type: '' });

  const loadAll = () => {
    partnerAPI.getDashboard().then(setStats).catch(console.error);
    partnerAPI.getProperties().then(setProperties).catch(console.error);
    partnerAPI.getEnquiries().then(setEnquiries).catch(console.error);
    partnerAPI.getPayments().then(setPayments).catch(console.error);
    reviewsAPI.getPartnerReviews().then(setReviews).catch(console.error);
    bookingsAPI.getPartner().then(setBookings).catch(console.error);
  };

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    if (user.role !== 'Partner' && user.role !== 'Admin') { navigate('/'); return; }
    loadAll();
  }, [user]);

  const STATUS_COLORS = {
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700'
  };

  const isUnapproved = user?.role === 'Partner' && !user?.is_approved;
  const hasFeePaid = user?.onboarding_fee_paid || payments.length > 0;

  return (
    <div data-testid="partner-dashboard" className="min-h-screen bg-[#FAFAF9] flex">
      <Sidebar active={active} setActive={setActive} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-navy"><Menu size={22} /></button>
            <div>
              <h1 className="font-semibold text-navy text-lg capitalize">{active === 'onboarding' ? 'Onboarding Fee' : active}</h1>
              <p className="text-gray-400 text-xs">Welcome back, {user?.name}</p>
            </div>
          </div>
          <button onClick={() => setActive('add')}
            className="bg-navy text-white text-sm font-medium px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-navy-light transition-colors">
            <Plus size={15} /> Add Property
          </button>
        </div>

        {/* Approval banner */}
        {isUnapproved && (
          <div className="mx-6 mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="approval-pending-banner">
            <Clock size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 text-sm">Account Pending Approval</p>
              <p className="text-amber-600 text-xs mt-0.5">Your account is under review. Add properties and pay the onboarding fee to get listed faster.</p>
            </div>
          </div>
        )}

        <div className="p-6 lg:p-8">
          {/* Overview */}
          {active === 'overview' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon={<Home size={20} />} label="Total Properties" value={stats?.total_properties ?? '—'} />
                <StatCard icon={<CheckCircle size={20} />} label="Approved" value={stats?.approved_properties ?? '—'} color="green" />
                <StatCard icon={<Calendar size={20} />} label="Total Bookings" value={bookings.length} color="blue" />
                <StatCard icon={<IndianRupee size={20} />} label="Revenue" value={bookings.filter(b=>b.payment_status==='paid').reduce((s,b)=>s+(b.total||0),0) > 0 ? `₹${bookings.filter(b=>b.payment_status==='paid').reduce((s,b)=>s+(b.total||0),0).toLocaleString('en-IN')}` : '₹0'} color="amber" />
              </div>

              {!hasFeePaid && (
                <div className="bg-navy rounded-2xl p-6 mb-6 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-gold text-xs uppercase tracking-[0.2em] font-medium mb-1">Action Required</p>
                    <h3 className="text-white font-semibold text-base">Pay Partner Onboarding Fee</h3>
                    <p className="text-white/60 text-sm">Pay the one-time fee to activate your listing and get verified.</p>
                  </div>
                  <button onClick={() => setActive('onboarding')}
                    className="bg-gold hover:bg-gold-dark text-white px-5 py-2.5 rounded-xl text-sm font-semibold shrink-0 transition-colors">
                    Pay Fee
                  </button>
                </div>
              )}

              {properties.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                  <Home size={36} className="text-gray-200 mx-auto mb-4" />
                  <h3 className="text-navy font-semibold mb-2">No properties yet</h3>
                  <p className="text-gray-400 text-sm mb-5">Start by adding your first property listing.</p>
                  <button onClick={() => setActive('add')}
                    className="bg-navy text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-navy-light transition-colors">
                    Add Your First Property
                  </button>
                </div>
              ) : (
                <div>
                  <h2 className="font-semibold text-navy mb-4">Recent Properties</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {properties.slice(0, 3).map(p => (
                      <div key={p.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                        <div className="h-36 bg-gray-100">
                          {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-medium text-navy text-sm truncate">{p.name}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                              {p.status}
                            </span>
                          </div>
                          <p className="text-gray-400 text-xs">{p.location?.city}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Properties */}
          {active === 'properties' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {properties.length === 0 ? (
                <div className="text-center py-20">
                  <Home size={36} className="text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No properties yet</p>
                  <button onClick={() => setActive('add')} className="bg-navy text-white px-6 py-3 rounded-xl text-sm">Add Property</button>
                </div>
              ) : (
                <div className="space-y-4">
                  {properties.map(p => (
                    <div key={p.id} data-testid={`partner-property-${p.id}`}
                      className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-5">
                      <div className="w-20 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-navy text-sm">{p.name}</h4>
                        <p className="text-gray-400 text-xs mt-0.5">{p.location?.city} · {TYPE_LABELS[p.type]}</p>
                        <p className="text-gold text-xs font-medium mt-1">₹{p.price_min?.toLocaleString('en-IN')}+</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs px-3 py-1 rounded-full capitalize ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                          {p.status}
                        </span>
                        {!p.fee_paid && (
                          <button
                            onClick={() => { setPayModal({ open: true, type: p.type, id: p.id }); }}
                            className="text-xs bg-gold/10 text-gold px-3 py-1 rounded-full hover:bg-gold/20 transition-colors border border-gold/20">
                            Pay Fee
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Add Property */}
          {active === 'add' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {propSuccess ? (
                <div className="text-center py-20 max-w-md mx-auto">
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                  <h3 className="font-serif text-2xl font-bold text-navy mb-2">All Done!</h3>
                  <p className="text-gray-400 text-sm mb-6">Your property is submitted and payment is complete. We'll review and activate it shortly.</p>
                  <button onClick={() => { setPropSuccess(false); setActive('properties'); loadAll(); }}
                    className="bg-navy text-white px-6 py-3 rounded-xl text-sm font-medium">View My Properties</button>
                </div>
              ) : (
                <AddPropertyForm user={user} onSuccess={() => { setPropSuccess(true); loadAll(); }} />
              )}
            </motion.div>
          )}

          {/* Bookings */}
          {active === 'bookings' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* Summary stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard icon={<Calendar size={20} />} label="Total Bookings" value={bookings.length} color="blue" />
                <StatCard icon={<CheckCircle size={20} />} label="Confirmed" value={bookings.filter(b=>b.status==='confirmed').length} color="green" />
                <StatCard icon={<Clock size={20} />} label="Pending" value={bookings.filter(b=>b.status==='pending').length} color="amber" />
                <StatCard icon={<IndianRupee size={20} />} label="Revenue" value={`₹${bookings.filter(b=>b.payment_status==='paid').reduce((s,b)=>s+(b.total||0),0).toLocaleString('en-IN')}`} />
              </div>
              {bookings.length === 0 ? (
                <div className="text-center py-20">
                  <Calendar size={36} className="text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No bookings yet</p>
                  <p className="text-gray-400 text-sm mt-1">Bookings will appear here once customers complete a payment.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bookings.map(b => (
                    <div key={b.id} data-testid={`partner-booking-${b.booking_id}`}
                      className="bg-white rounded-2xl border border-gray-100 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-navy text-sm">{b.customer_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                              b.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              b.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                            }`}>{b.status}</span>
                          </div>
                          <p className="text-gold text-xs font-medium font-mono">{b.booking_id}</p>
                          <p className="text-gray-400 text-xs">{b.property_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-navy">₹{(b.total||0).toLocaleString('en-IN')}</p>
                          <span className={`text-xs ${b.payment_status==='paid'?'text-green-600':'text-amber-600'}`}>{b.payment_status}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-500">
                        <div><span className="block text-gray-400">Type</span><span className="capitalize">{b.experience_type}</span></div>
                        <div><span className="block text-gray-400">{b.experience_type==='stay'?'Check-in':'Event Date'}</span>{b.check_in||b.event_date||'—'}</div>
                        <div><span className="block text-gray-400">Guests</span>{b.guests}</div>
                        <div><span className="block text-gray-400">Phone</span>{b.customer_phone}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Enquiries */}
          {active === 'enquiries' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {enquiries.length === 0 ? (
                <div className="text-center py-20">
                  <MessageSquare size={36} className="text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-400 text-sm">No enquiries yet. Email alerts will be sent when customers enquire.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {enquiries.map(e => (
                    <div key={e.id} data-testid={`enquiry-${e.id}`}
                      className="bg-white rounded-2xl border border-gray-100 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-navy text-sm">{e.name}</h4>
                          <p className="text-gray-400 text-xs">{e.property_name}</p>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full ${e.status === 'new' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                          {e.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs text-gray-500">
                        <div><span className="block text-gray-400">Email</span>{e.email}</div>
                        <div><span className="block text-gray-400">Phone</span>{e.phone}</div>
                        <div><span className="block text-gray-400">Guests</span>{e.guests}</div>
                      </div>
                      {e.message && <p className="text-gray-500 text-xs mt-3 bg-gray-50 p-3 rounded-lg">{e.message}</p>}
                      <p className="text-gray-300 text-xs mt-3">{new Date(e.created_at).toLocaleDateString('en-IN')}</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Reviews */}
          {active === 'reviews' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-semibold text-navy text-lg">Guest Reviews</h2>
                  <p className="text-gray-400 text-xs mt-0.5">Reply to approved reviews to show guests you care</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-gold/10 text-gold text-xs font-medium px-3 py-1 rounded-full">
                    {reviews.filter(r => r.status === 'approved').length} approved
                  </span>
                  <span className="bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                    {reviews.filter(r => r.status === 'approved' && !r.reply).length} need reply
                  </span>
                </div>
              </div>
              {reviews.length === 0 ? (
                <div className="text-center py-20">
                  <Star size={36} className="text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No reviews yet</p>
                  <p className="text-gray-400 text-sm mt-1">Reviews from guests will appear here after admin approval.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map(r => (
                    <div key={r.id} data-testid={`partner-review-${r.id}`}
                      className="bg-white rounded-2xl border border-gray-100 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-semibold text-navy text-sm">{r.customer_name}</span>
                            <StarRating value={r.rating} readOnly size="sm" />
                            <span className="text-gray-400 text-xs">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
                          </div>
                          <p className="text-gold text-xs font-medium">{r.property_name}</p>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full capitalize shrink-0 ml-2 ${
                          r.status === 'approved' ? 'bg-green-100 text-green-700' :
                          r.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                        }`}>{r.status}</span>
                      </div>
                      {r.title && <p className="font-medium text-navy text-sm mb-1">{r.title}</p>}
                      <p className="text-gray-500 text-sm">{r.comment}</p>
                      {/* Inline reply */}
                      <ReviewReplyRow
                        review={r}
                        onUpdate={(id, updates) => setReviews(prev => prev.map(x => x.id === id ? { ...x, ...updates } : x))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Onboarding Fee */}
          {active === 'onboarding' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg">
              {/* Status card */}
              <div className={`rounded-2xl p-8 text-white mb-6 ${hasFeePaid ? 'bg-green-600' : 'bg-navy'}`}>
                <p className="text-white/70 text-xs uppercase tracking-[0.2em] mb-3">
                  {hasFeePaid ? 'Payment Complete' : 'Payment Required'}
                </p>
                <h3 className="font-serif text-3xl font-bold mb-2">Partner Onboarding Fee</h3>
                <p className="text-white/60 text-sm">
                  {hasFeePaid
                    ? 'Your one-time onboarding fee has been received. Your listing is active.'
                    : 'Pay once to get your property verified and listed on HYGAD.'}
                </p>
                {hasFeePaid && (
                  <div className="mt-4 flex items-center gap-2">
                    <CheckCircle size={18} className="text-white" />
                    <span className="text-white font-medium text-sm">Verified Partner</span>
                  </div>
                )}
              </div>

              {/* Payment history */}
              {payments.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
                  <h4 className="font-semibold text-navy mb-4">Payment History</h4>
                  <div className="space-y-3">
                    {payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-navy capitalize">{TYPE_LABELS[p.property_type] || p.property_type}</p>
                          <p className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString('en-IN')}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gold">₹{(p.amount || 0).toLocaleString('en-IN')}</p>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Paid</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fee table */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
                <h4 className="font-semibold text-navy mb-4">One-Time Partner Onboarding Fees</h4>
                <div className="space-y-2">
                  {Object.entries(ONBOARDING_FEES).map(([type, fee]) => (
                    <div key={type} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-600">{TYPE_LABELS[type]}</span>
                      <span className="font-semibold text-gold">₹{fee.toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {!hasFeePaid && (
                <div>
                  <p className="text-sm text-gray-500 mb-4">Select property type to pay onboarding fee:</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {PROPERTY_TYPES.map(type => (
                      <button key={type} data-testid={`pay-fee-${type}`}
                        onClick={() => setPayModal({ open: true, type, id: null })}
                        className="flex items-center justify-between p-4 bg-white border border-gray-200 hover:border-gold rounded-xl transition-colors text-sm">
                        <span className="text-navy font-medium">{TYPE_LABELS[type]}</span>
                        <span className="text-gold font-bold">₹{ONBOARDING_FEES[type]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Profile */}
          {active === 'profile' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg">
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center text-white text-2xl font-bold mb-5">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <h3 className="font-serif text-2xl font-bold text-navy mb-6">{user?.name}</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Email', value: user?.email },
                    { label: 'Phone', value: user?.phone || 'Not provided' },
                    { label: 'Role', value: user?.role },
                    { label: 'Account Status', value: user?.is_approved ? 'Approved' : 'Pending Approval' },
                    { label: 'Onboarding Fee', value: hasFeePaid ? 'Paid' : 'Pending' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between py-3 border-b border-gray-50">
                      <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
                      <span className={`text-sm font-medium ${label === 'Onboarding Fee' ? (hasFeePaid ? 'text-green-600' : 'text-amber-600') : 'text-navy'}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Payment modal for property list row */}
      <PaymentModal
        isOpen={payModal.open}
        onClose={() => setPayModal({ open: false, type: '' })}
        propertyType={payModal.type}
        propertyId={payModal.id}
        partnerName={user?.name}
        partnerEmail={user?.email}
        onSuccess={() => { setPayModal({ open: false, type: '' }); loadAll(); }}
      />
    </div>
  );
};

export default PartnerDashboard;
